import API_CONFIG from '../config/api';

// Types
export interface BankPaymentRequest {
  _id: string;
  userId: {
    _id: string;
    name: string;
    lastName: string;
    mobile: string;
  };
  patientInfo: {
    admissionNumber: string;
    nationalId: string;
    firstName: string;
    lastName: string;
    testDate: string;
  };
  receiptImage: string;
  description?: string;
  status: 'pending' | 'approved' | 'rejected' | 'no_payment_needed' | 'paid';
  statusDisplay: string;
  adminResponse?: {
    responseDate: string;
    paymentAmount?: number;
    rejectionReason?: string;
    respondedBy?: {
      _id: string;
      name: string;
      lastName: string;
    };
  };
  paymentInfo?: {
    paymentUrl?: string;
    paymentId?: string;
    paymentStatus: 'pending' | 'completed' | 'failed';
    paidAt?: string;
    transactionId?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface BankPaymentRequestStats {
  totalRequests: number;
  pendingRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  noPaymentNeededRequests: number;
  requestsToday: number;
}

export interface CreateBankPaymentRequestData {
  patientInfo: {
    admissionNumber: string;
    nationalId: string;
    firstName: string;
    lastName: string;
    testDate: string;
  };
  description?: string;
  receiptImage: File;
}

export interface UpdateBankPaymentRequestStatusData {
  status: 'approved' | 'rejected' | 'no_payment_needed';
  paymentAmount?: number;
  rejectionReason?: string;
}

export interface BankPaymentResponse {
  success: boolean;
  message: string;
  data?: BankPaymentRequest | BankPaymentRequest[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface BankPaymentStatsResponse {
  success: boolean;
  data: BankPaymentRequestStats;
}

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  };
};

// Create a new bank payment request
export const createBankPaymentRequest = async (
  requestData: CreateBankPaymentRequestData
): Promise<BankPaymentResponse> => {
  const formData = new FormData();
  
  // Add patient info
  formData.append('patientInfo[admissionNumber]', requestData.patientInfo.admissionNumber);
  formData.append('patientInfo[nationalId]', requestData.patientInfo.nationalId);
  formData.append('patientInfo[firstName]', requestData.patientInfo.firstName);
  formData.append('patientInfo[lastName]', requestData.patientInfo.lastName);
  formData.append('patientInfo[testDate]', requestData.patientInfo.testDate);
  
  // Add description if provided
  if (requestData.description) {
    formData.append('description', requestData.description);
  }
  
  // Add receipt image
  formData.append('receiptImage', requestData.receiptImage);

  const response = await fetch(`${API_CONFIG.baseURL}/bank-payments/create`, {
    method: 'POST',
    ...getAuthHeaders(),
    body: formData,
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در ثبت درخواست');
  }

  return data;
};

// Get user's bank payment requests
export const getUserBankPaymentRequests = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
}): Promise<BankPaymentResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.status) queryParams.append('status', params.status);

  const response = await fetch(
    `${API_CONFIG.baseURL}/bank-payments/my-requests?${queryParams.toString()}`,
    {
      method: 'GET',
      ...getAuthHeaders(),
    }
  );

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در دریافت درخواست‌ها');
  }

  return data;
};

// Get bank payment request by ID
export const getBankPaymentRequestById = async (
  requestId: string
): Promise<BankPaymentResponse> => {
  const response = await fetch(
    `${API_CONFIG.baseURL}/bank-payments/${requestId}`,
    {
      method: 'GET',
      ...getAuthHeaders(),
    }
  );

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در دریافت درخواست');
  }

  return data;
};

// Get all bank payment requests (admin only)
export const getAllBankPaymentRequests = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}): Promise<BankPaymentResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.status) queryParams.append('status', params.status);
  if (params?.search) queryParams.append('search', params.search);

  const response = await fetch(
    `${API_CONFIG.baseURL}/bank-payments/admin/all?${queryParams.toString()}`,
    {
      method: 'GET',
      ...getAuthHeaders(),
    }
  );

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در دریافت درخواست‌ها');
  }

  return data;
};

// Update bank payment request status (admin only)
export const updateBankPaymentRequestStatus = async (
  requestId: string,
  statusData: UpdateBankPaymentRequestStatusData
): Promise<BankPaymentResponse> => {
  const response = await fetch(
    `${API_CONFIG.baseURL}/bank-payments/admin/${requestId}/status`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders().headers,
      },
      body: JSON.stringify(statusData),
    }
  );

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در به‌روزرسانی وضعیت درخواست');
  }

  return data;
};

// Get bank payment request statistics (admin only)
export const getBankPaymentRequestStats = async (): Promise<BankPaymentStatsResponse> => {
  const response = await fetch(
    `${API_CONFIG.baseURL}/bank-payments/admin/stats`,
    {
      method: 'GET',
      ...getAuthHeaders(),
    }
  );

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در دریافت آمار درخواست‌ها');
  }

  return data;
};